import { useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import "./billingPage.css";
import { CombinedContext } from "../../context/combinedContext";
import billingRequest from "../../request/billing";
import translations from "../../translations/translations";

/* Paddle's default payment link resolves to this route, so `/billing?_ptxn=…`
   is where a customer lands to finish a transaction they did not complete on
   /checkout: a retry after a declined card, a link out of a Paddle email, or a
   bank sending them back from a 3-D Secure challenge. ProtectedRoute waves any
   request carrying `_ptxn` straight through without waiting for auth, so
   nothing below may assume userData is populated — it may arrive late, or not
   at all. */
const BillingPage = () => {
	const { userData, language } = useContext(CombinedContext);
	const location = useLocation();

	const transactionId = new URLSearchParams(location.search).get("_ptxn");

	const [checkoutOpen, setCheckoutOpen] = useState(false);
	const [unavailable, setUnavailable] = useState(false);

	const openedRef = useRef(false);
	const intervalRef = useRef(null);

	const t = (translations[language] || translations.en).checkoutPage;

	const shopId = userData?.shop?.shopId;
	const accessToken = userData?.cognitoTokens?.AccessToken;

	useEffect(() => {
		if (!transactionId) return;
		if (openedRef.current) return;

		const paddleLocaleMap = { en: "en", es: "es", fr: "fr" };
		const paddleLocale = paddleLocaleMap[language] || "en";

		let readyPoll = null;
		let waited = 0;

		const isPaddleReady = () => window.__PADDLE_READY__ && window.Paddle?.Checkout?.open;

		const openCheckout = () => {
			openedRef.current = true;

			/* Overlay rather than inline: Paddle owns the modal's sizing and its
			   scrolling, so a 3-D Secure challenge taller than the card form
			   cannot be clipped by this page's layout the way an inline frame in
			   a fixed-height box can be. There is no surrounding content on this
			   route worth keeping visible, so there is nothing to trade away. */
			window.Paddle.Checkout.open({
				settings: {
					displayMode: "overlay",
					locale: paddleLocale,
				},
				transactionId,
			});

			setCheckoutOpen(true);
		};

		if (isPaddleReady()) {
			openCheckout();
			return;
		}

		// Bounded, as on /checkout: if Paddle is blocked by an adblocker or its
		// CDN is down, say so rather than spinning for the life of the page.
		readyPoll = setInterval(() => {
			waited += 100;

			if (isPaddleReady()) {
				clearInterval(readyPoll);
				readyPoll = null;
				openCheckout();
				return;
			}

			if (waited >= 15000) {
				clearInterval(readyPoll);
				readyPoll = null;
				console.error("Paddle failed to load — checkout could not open");
				setUnavailable(true);
			}
		}, 100);

		return () => {
			if (readyPoll) clearInterval(readyPoll);
		};
	}, [transactionId, language]);

	/* Activation is written by the Paddle webhook, not by the browser, so the
	   server is the only thing that can say the shop went live. Keyed on the
	   credentials as well as the checkout so that a userData that resolves
	   after ProtectedRoute let us in still starts the poll. An unauthenticated
	   return has nothing to poll with and leans on Paddle's own success screen. */
	useEffect(() => {
		if (!checkoutOpen || !shopId || !accessToken) return;

		const startedAt = Date.now();

		intervalRef.current = setInterval(async () => {
			if (Date.now() - startedAt >= 15 * 60 * 1000) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
				return;
			}

			try {
				const status = await billingRequest.billingStatus(shopId, accessToken);

				if (status.active) {
					clearInterval(intervalRef.current);
					intervalRef.current = null;
					window.location.href = "/calendar?showLiveBanner=true";
				}
			} catch (err) {
				console.error("Polling error:", err);
			}
		}, 4000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [checkoutOpen, shopId, accessToken]);

	// With no transaction to resume this route has no job of its own — the real
	// billing screen lives in the account section.
	if (!transactionId) return <Navigate to="/account/billing" replace />;

	return (
		<div className="sf-billing-resume">
			<div className="sf-billing-resume-card">
				<h1>{t.activateTitle}</h1>
				<p>{unavailable ? t.checkoutUnavailable : t.activateSubtitle}</p>

				{unavailable && (
					<button className="sf-billing-resume-btn" onClick={() => window.location.reload()}>
						{t.retry}
					</button>
				)}
			</div>
		</div>
	);
};

export default BillingPage;
